import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { getCurrentUser } from 'aws-amplify/auth';

const CreateUserLessonModal = ({ 
  open, 
  onClose, 
  onSave, 
  lessonsOptions, 
  existingUserLessons 
}) => {
  const [mode, setMode] = useState('create'); // 'create' or 'duplicate'
  const [selectedUserLesson, setSelectedUserLesson] = useState(null);
  const [formData, setFormData] = useState({
    userID: '',
    email: '',
    firstName: '',
    lastName: '',
    lessonID: '',
    progress: 0,
    startedAt: null,
    finishedAt: null,
    dateOfBirth: null
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [cognitoUserInfo, setCognitoUserInfo] = useState(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setMode('create');
    setSelectedUserLesson(null);
    setFormData({
      userID: '',
      email: '',
      firstName: '',
      lastName: '',
      lessonID: '',
      progress: 0,
      startedAt: null,
      finishedAt: null,
      dateOfBirth: null
    });
    setErrors({});
    setCognitoUserInfo(null);
  };

  const handleModeChange = (event) => {
    const newMode = event.target.value;
    setMode(newMode);
    if (newMode === 'create') {
      setSelectedUserLesson(null);
      resetFormFields();
    }
  };

  const resetFormFields = () => {
    setFormData({
      userID: '',
      email: '',
      firstName: '',
      lastName: '',
      lessonID: '',
      progress: 0,
      startedAt: null,
      finishedAt: null,
      dateOfBirth: null
    });
    setCognitoUserInfo(null);
  };

  const handleDuplicateSelection = (event, value) => {
    setSelectedUserLesson(value);
    if (value) {
      // Prefill form with selected record
      setFormData({
        userID: value.userID || '',
        email: value.email || '',
        firstName: value.firstName || '',
        lastName: value.lastName || '',
        lessonID: value.lessonID || '',
        progress: value.progress || 0,
        startedAt: value.startedAt ? dayjs(value.startedAt) : null,
        finishedAt: value.finishedAt ? dayjs(value.finishedAt) : null,
        dateOfBirth: value.dateOfBirth ? dayjs(value.dateOfBirth) : null
      });
    } else {
      resetFormFields();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }

    // If email is being entered, try to lookup Cognito user
    if (field === 'email' && value && value.includes('@')) {
      lookupCognitoUser(value);
    }
  };

  const lookupCognitoUser = async (email) => {
    try {
      setLoading(true);
      
      // Try to get current user first to see if it matches
      const currentUser = await getCurrentUser();
      if (currentUser.signInDetails?.loginId === email) {
        const userInfo = {
          sub: currentUser.userId,
          email: currentUser.signInDetails?.loginId || '',
          given_name: currentUser.username || '', // fallback to username since attributes may not be available
          family_name: '',
          birthdate: null
        };
        setCognitoUserInfo(userInfo);
        
        // Auto-populate fields
        setFormData(prev => ({
          ...prev,
          userID: userInfo.sub,
          firstName: userInfo.given_name,
          lastName: userInfo.family_name,
          dateOfBirth: userInfo.birthdate ? dayjs(userInfo.birthdate) : null
        }));
        return;
      }

      // For admin usage, we'd need to implement user lookup via Admin API
      // For now, clear any previous auto-populated data
      setCognitoUserInfo(null);
      
    } catch (error) {
      console.log('No matching Cognito user found or not authorized:', error);
      setCognitoUserInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.userID.trim()) {
      newErrors.userID = 'El ID de usuario es requerido';
    }

    if (!formData.lessonID) {
      newErrors.lessonID = 'La lección es requerida';
    }

    if (formData.progress === 100 && !formData.finishedAt) {
      // Auto-set finishedAt if progress is 100%
      setFormData(prev => ({
        ...prev,
        finishedAt: dayjs()
      }));
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    const userLessonData = {
      userID: formData.userID.trim(),
      lessonID: formData.lessonID,
      progress: formData.progress,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      startedAt: formData.startedAt ? formData.startedAt.toISOString() : dayjs().toISOString(),
      finishedAt: formData.finishedAt ? formData.finishedAt.toISOString() : null,
      dateOfBirth: formData.dateOfBirth ? formData.dateOfBirth.format('YYYY-MM-DD') : null
    };

    try {
      await onSave(userLessonData);
      onClose();
      resetForm();
    } catch (error) {
      console.error('Error saving user lesson:', error);
    }
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  // Create options for duplicate selection
  const duplicateOptions = existingUserLessons.map(ul => ({
    ...ul,
    label: `${ul.email || 'Sin email'} - ${ul.lessonTitle || 'Lección sin título'}`,
    searchText: `${ul.email || ''} ${ul.firstName || ''} ${ul.lastName || ''} ${ul.lessonTitle || ''}`
  }));

  const progressOptions = [
    { value: 0, label: 'No iniciado (0%)' },
    { value: 50, label: 'En progreso (50%)' },
    { value: 100, label: 'Completado (100%)' }
  ];

  return (
    <LocalizationProvider 
      dateAdapter={AdapterDayjs} 
      adapterLocale="es"
    >
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '600px' }
        }}
      >
        <DialogTitle>
          <Typography variant="h6">
            {mode === 'create' ? 'Crear lección de usuario' : 'Duplicar lección de usuario'}
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Warning Alert */}
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Herramientas avanzadas: uso con precaución.</strong>
                <br />
                Esta acción puede crear duplicados a propósito para pruebas.
              </Typography>
            </Alert>

            {/* Mode Selection */}
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Modo</FormLabel>
              <RadioGroup
                row
                value={mode}
                onChange={handleModeChange}
              >
                <FormControlLabel 
                  value="create" 
                  control={<Radio />} 
                  label="Crear desde cero" 
                />
                <FormControlLabel 
                  value="duplicate" 
                  control={<Radio />} 
                  label="Duplicar existente" 
                />
              </RadioGroup>
            </FormControl>

            {/* Duplicate Selection */}
            {mode === 'duplicate' && (
              <Box sx={{ mb: 3 }}>
                <Autocomplete
                  options={duplicateOptions}
                  getOptionLabel={(option) => option.label}
                  value={selectedUserLesson}
                  onChange={handleDuplicateSelection}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Selecciona una lección de usuario para duplicar"
                      placeholder="Buscar por email o título de lección..."
                      fullWidth
                    />
                  )}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter(option =>
                      option.searchText.toLowerCase().includes(inputValue.toLowerCase())
                    );
                  }}
                />
              </Box>
            )}

            <Grid container spacing={2}>
              {/* User Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Información del Usuario
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Usuario (ID de Cognito o Email)"
                  value={formData.userID}
                  onChange={(e) => handleInputChange('userID', e.target.value)}
                  error={!!errors.userID}
                  helperText={errors.userID}
                  placeholder="sub-xxxx-xxxx o email@ejemplo.com"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="usuario@ejemplo.com"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombre"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Apellido"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Fecha de nacimiento"
                  value={formData.dateOfBirth}
                  onChange={(value) => handleInputChange('dateOfBirth', value)}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth />
                  )}
                />
              </Grid>

              {/* Lesson Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 2, mt: 2, fontWeight: 600 }}>
                  Información de la Lección
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.lessonID}>
                  <InputLabel>Lección *</InputLabel>
                  <Select
                    value={formData.lessonID}
                    label="Lección *"
                    onChange={(e) => handleInputChange('lessonID', e.target.value)}
                  >
                    {lessonsOptions.map((lesson) => (
                      <MenuItem key={lesson.id} value={lesson.id}>
                        {lesson.title}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.lessonID && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                      {errors.lessonID}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Progreso</InputLabel>
                  <Select
                    value={formData.progress}
                    label="Progreso"
                    onChange={(e) => handleInputChange('progress', e.target.value)}
                  >
                    {progressOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Fecha de inicio"
                  value={formData.startedAt}
                  onChange={(value) => handleInputChange('startedAt', value)}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Fecha de finalización"
                  value={formData.finishedAt}
                  onChange={(value) => handleInputChange('finishedAt', value)}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth />
                  )}
                />
              </Grid>
            </Grid>

            {/* Cognito User Info Display */}
            {cognitoUserInfo && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Usuario encontrado en Cognito:</strong><br />
                  {cognitoUserInfo.given_name} {cognitoUserInfo.family_name} ({cognitoUserInfo.email})
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} color="secondary">
            Cancelar
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateUserLessonModal;